// Generic client-side "Quick Filter" for any <table class="data-table"> or
// <table data-qf> (the shadcn <Table> component sets data-qf for you).
//
// Two entry points, one engine:
//   • Right-click a body cell  → "Filter by <column>: <value>" (exact match)
//   • Left-click a column HEADER → the column filter panel: text contains,
//     multi-select value list, numeric compare (=, ≠, >, ≥, <, ≤ "up to",
//     between) and date range.
//
// It is a purely VISUAL filter — it toggles a class on <tr> nodes and never
// touches page or React state — which is exactly why it works on every
// data-table in the app with zero per-page wiring.
//
// Consumers:
//   • ErpContextMenu.jsx    — describeRow() / applyFilter(), and
//                             selectionExport() / tableExport() for Create Doc
//   • ColumnFilterMenu.jsx  — describeColumn() / setFilter() (the header panel)
//   • QuickFilterBar.jsx    — subscribe()/getActive()/removeFilter()/clearAll()
//
// Caveats (accepted):
//   • Only rows currently in the DOM are filtered. On server-paginated tables
//     (Sales/Heats) that's just the loaded page.
//   • Row-spanning "group" tables (e.g. the Sales Invoices Date column):
//     filtering by a non-group column can hide a group's anchor row and orphan
//     its label. Filtering BY the group column itself works cleanly.

const HIDDEN_CLASS = "qf-hidden";
const COL_ACTIVE_CLASS = "qf-col-active";
const PICK_CLASS = "qf-picked";

// `.data-table` = the raw-HTML tables; `[data-qf]` = the shadcn <Table>
// component, which opts in for free (see components/ui/table.jsx). The
// selector is deliberately separate from the styling class so a table can be
// filterable without inheriting .data-table's looks.
export const TABLE_SELECTOR = "table.data-table, table[data-qf]";

// Collapse whitespace so multi-line grouped cells compare/display cleanly.
const norm = (s) => (s || "").replace(/\s+/g, " ").trim();

// Cells that carry no value. Never worth offering as a filter target, and what
// "Is blank" matches.
const PLACEHOLDERS = new Set(["", "-", "—", "–", "na", "n/a", "nil", "null"]);
const isBlankText = (s) => PLACEHOLDERS.has(norm(s).toLowerCase());

// ── Value parsing ──────────────────────────────────────────────────────────
// Cell text → number, strict: the whole cell must be a number once currency
// symbols, unit suffixes and grouping commas are stripped. Strictness is the
// point — it stops "2627/S-70" or "FY 2026-27" from being read as numeric and
// mis-typing the column.
const CURRENCY_RE = /[₹$€£¥]/g;
const UNIT_RE = /\b(kgs?|mts?|tons?|tonnes?|pcs?|nos?|units?|hrs?|days?)\b/gi;

export function parseNumber(text) {
  let t = norm(text).replace(CURRENCY_RE, "").replace(UNIT_RE, "").replace(/%/g, "").trim();
  if (!t) return null;
  let neg = false;
  if (/^\(.+\)$/.test(t)) { neg = true; t = t.slice(1, -1).trim(); } // (1,234) = -1234
  if (/^[-−]/.test(t)) { neg = true; t = t.slice(1).trim(); }
  else if (t.startsWith("+")) t = t.slice(1).trim();
  t = t.replace(/[, \s]/g, "");
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

// Cell text → UTC epoch ms. Scans rather than anchors so grouped cells like
// "20/05/2026 2 invoices" (the Sales Invoices date column) still resolve.
const YMD_RE = /(\d{4})-(\d{2})-(\d{2})/;
const DMY_RE = /(\d{1,2})[/-](\d{1,2})[/-](\d{4})/;

export function parseDate(text) {
  const t = norm(text);
  if (!t) return null;
  let m = t.match(YMD_RE);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]);
  m = t.match(DMY_RE);
  if (m) {
    const d = +m[1], mo = +m[2];
    if (d < 1 || d > 31 || mo < 1 || mo > 12) return null;
    return Date.UTC(+m[3], mo - 1, d);
  }
  return null;
}

export function fmtDate(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

// The string a cell contributes to the value list / "is one of" matching. Date
// columns collapse to just the date so grouped cells don't each become their
// own distinct value.
function displayValue(raw, kind) {
  if (kind === "date") {
    const d = parseDate(raw);
    if (d !== null) return fmtDate(d);
  }
  return norm(raw);
}

// ── Cell text ──────────────────────────────────────────────────────────────
// What a cell contributes to filtering. A cell may override its visible text
// with `data-qf-text` — used by grouped tables whose merged group cell shows a
// short label ("KOLHAPUR") but must filter as the full location
// ("KOLHAPUR · MAHARASHTRA") so a filter on the parent group still selects it.
export function cellText(cell) {
  if (!cell) return "";
  const override = cell.dataset ? cell.dataset.qfText : undefined;
  return norm(override !== undefined ? override : cell.textContent);
}

// A group-header row (`data-qf-group-header`) labels the section beneath it and
// holds no per-row data of its own. A summary row (`data-qf-summary`, e.g. an
// order's TOTAL line) aggregates the section above it — also not per-row data,
// so it's skipped by values/stats/export the same way.
function isGroupHeaderRow(row) {
  return !!(row && row.hasAttribute && (row.hasAttribute("data-qf-group-header") || row.hasAttribute("data-qf-summary")));
}

// A body row that holds data and is on screen: not a group banner, not hidden
// by a filter, not a lone full-width "No records" / note row.
function isDataRow(row) {
  if (!row || isGroupHeaderRow(row) || row.classList.contains(HIDDEN_CLASS)) return false;
  return !(row.cells.length === 1 && (row.cells[0].colSpan || 1) > 1);
}

// ── Grid mapping ───────────────────────────────────────────────────────────
// Resolve rowspan/colspan into a logical grid where grid[r][c] points at the
// cell occupying that column. This is the only correct way to map a clicked
// <td> to its header (and to find a row's cell in a given column) once a table
// uses row-spanning group cells that shift later cells rightward.
function buildGrid(rows) {
  const grid = [];
  for (let r = 0; r < rows.length; r++) {
    if (!grid[r]) grid[r] = [];
    let c = 0;
    for (const cell of rows[r].cells) {
      while (grid[r][c] !== undefined) c++;
      const cs = cell.colSpan || 1;
      const rs = cell.rowSpan || 1;
      for (let dr = 0; dr < rs; dr++) {
        for (let dc = 0; dc < cs; dc++) {
          if (!grid[r + dr]) grid[r + dr] = [];
          grid[r + dr][c + dc] = cell;
        }
      }
      c += cs;
    }
  }
  return grid;
}

// Logical column of a header cell (leftmost column for a colspan), or -1.
function headerColIndex(table, th) {
  const allRows = Array.from(table.rows);
  const rowIdx = allRows.indexOf(th.parentElement);
  if (rowIdx < 0) return -1;
  return (buildGrid(allRows)[rowIdx] || []).indexOf(th);
}

// Body cell of every row in `colIndex`, row-aligned (a rowspan cell repeats for
// each row it covers, so counts are per-row not per-group).
function columnCells(table, colIndex) {
  const tbody = table.tBodies[0];
  if (!tbody) return [];
  const rows = Array.from(tbody.rows);
  const grid = buildGrid(rows);
  // Group headers describe a section, not a row — they'd otherwise show up as
  // bogus one-off values in the column's distinct-value list.
  return rows
    .map((row, r) => (isGroupHeaderRow(row) ? null : grid[r] ? grid[r][colIndex] : null))
    .filter((c) => c !== null);
}

// Classify a column from its filled cell texts. Dates are checked first: a date
// never parses as a number, but a bare year would, so order matters.
const KIND_SAMPLE = 300;

function detectKind(filled) {
  const sample = filled.slice(0, KIND_SAMPLE);
  if (!sample.length) return "text";
  const dates = sample.filter((t) => parseDate(t) !== null).length;
  if (dates / sample.length >= 0.6) return "date";
  const nums = sample.filter((t) => parseNumber(t) !== null).length;
  if (nums / sample.length >= 0.6) return "number";
  return "text";
}

// Aggregate a numeric column over the rows currently VISIBLE — i.e. after any
// active filter, so the numbers always describe what's on screen. Returns null
// when the column holds nothing numeric.
export function columnStats(table, colIndex) {
  if (!table || !table.tBodies || !table.tBodies[0]) return null;
  const rows = Array.from(table.tBodies[0].rows);
  const grid = buildGrid(rows);
  const counted = new Set(); // a rowspan group cell must only be added once
  let sum = 0, count = 0, min = null, max = null, visible = 0;
  for (let r = 0; r < rows.length; r++) {
    if (rows[r].classList.contains(HIDDEN_CLASS) || isGroupHeaderRow(rows[r])) continue;
    visible++;
    const cell = grid[r] ? grid[r][colIndex] : null;
    if (!cell || counted.has(cell)) continue;
    counted.add(cell);
    const n = parseNumber(cellText(cell));
    if (n === null) continue;
    sum += n;
    count++;
    if (min === null || n < min) min = n;
    if (max === null || n > max) max = n;
  }
  if (!count) return null;
  return { sum, count, avg: sum / count, min, max, visibleRows: visible };
}

// ── Column inventory ───────────────────────────────────────────────────────
// Every filterable column of a table as { colIndex, label, kind }. `kind`
// drives the Math / Compare tabs of the filter flyout — they only offer
// columns that actually hold numbers.
export function describeColumns(table) {
  if (!table || !table.tHead || !table.tHead.rows.length || !table.tBodies[0]) return [];
  const grid = buildGrid(Array.from(table.rows));
  const headerRow = grid[table.tHead.rows.length - 1] || []; // deepest header row
  const out = [];
  const seen = new Set(); // a colspan header fills several columns — list it once
  for (let c = 0; c < headerRow.length; c++) {
    const hc = headerRow[c];
    if (!hc || seen.has(hc)) continue;
    seen.add(hc);
    const label = norm(hc.textContent);
    if (!label || /^actions?$/i.test(label)) continue;
    const filled = columnCells(table, c)
      .map((bc) => cellText(bc))
      .filter((t) => !isBlankText(t));
    out.push({ colIndex: c, label, kind: detectKind(filled) });
  }
  return out;
}

// ── Right-click: describe the row under the cursor ─────────────────────────
// Describe every filterable column of the row that `el` sits in, or null if
// `el` isn't inside a filterable body cell. The column the user actually
// clicked is flagged `clicked: true` and sorted first, so the menu can lead
// with it while still offering the whole row.
export function describeRow(el) {
  try {
    if (!(el instanceof Element)) return null;
    const cell = el.closest("td");
    if (!cell) return null;
    const table = cell.closest(TABLE_SELECTOR);
    if (!table) return null;
    if (cell.closest("thead")) return null; // header cells aren't filterable
    if (!table.tHead || !table.tHead.rows.length) return null; // need column names

    const allRows = Array.from(table.rows);
    const rowIdx = allRows.indexOf(cell.parentElement);
    if (rowIdx < 0) return null;
    const bodyRow = buildGrid(allRows)[rowIdx] || [];

    const columns = [];
    const seen = new Set(); // a colspan cell fills several columns — list it once
    for (const col of describeColumns(table)) {
      const dc = bodyRow[col.colIndex];
      if (!dc || seen.has(dc)) continue;
      seen.add(dc);
      const value = cellText(dc);
      if (isBlankText(value) || value.length > 60) continue;
      // `data-qf-match="contains"` lets a group header ("MAHARASHTRA") select
      // every row of its section, whose own cells read "PUNE · MAHARASHTRA".
      const match = (dc.dataset && dc.dataset.qfMatch) || "eq";
      columns.push({ ...col, value, match, number: parseNumber(value), clicked: dc === cell });
    }
    if (!columns.length) return null;
    columns.sort((a, b) => Number(b.clicked) - Number(a.clicked)); // clicked first
    return { table, columns };
  } catch {
    return null;
  }
}

// ── Header click: describe one column ──────────────────────────────────────
// Returns { table, colIndex, label, kind, values, count, min, max } where kind
// is "number" | "date" | "text" (detected by sampling the column) and `values`
// is the distinct value list with per-row counts, most common first.
export function describeColumn(el) {
  try {
    if (!(el instanceof Element)) return null;
    const th = el.closest("th");
    if (!th || !th.closest("thead")) return null;
    const table = th.closest(TABLE_SELECTOR);
    if (!table || !table.tHead || !table.tBodies[0]) return null;

    const label = norm(th.textContent);
    if (!label || /^actions?$/i.test(label)) return null;

    const colIndex = headerColIndex(table, th);
    if (colIndex < 0) return null;

    const cells = columnCells(table, colIndex);
    const texts = cells.map((c) => cellText(c));
    const filled = texts.filter((t) => !isBlankText(t));

    const kind = detectKind(filled);

    const counts = new Map();
    let blanks = 0;
    let min = null, max = null;
    for (const t of texts) {
      if (isBlankText(t)) { blanks++; continue; }
      const v = displayValue(t, kind);
      counts.set(v, (counts.get(v) || 0) + 1);
      if (kind === "number") {
        const n = parseNumber(t);
        if (n !== null) {
          if (min === null || n < min) min = n;
          if (max === null || n > max) max = n;
        }
      }
    }
    const values = Array.from(counts, ([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));

    return { table, colIndex, label, kind, values, blanks, rowCount: texts.length, min, max };
  } catch {
    return null;
  }
}

// ── Cell selection (Alt + right-click) ─────────────────────────────────────
// Alt + right-clicking body cells builds a scratch selection you can then do
// arithmetic on (sum / difference / average) or turn into a filter ("between
// these two", "is one of these"). Selection is scoped to one table; picking a
// cell in a different table starts over.
const picks = { items: [] };

// The header label + column index for a body cell.
function cellColumn(table, cell) {
  const thead = table.tHead;
  const allRows = Array.from(table.rows);
  const rowIdx = allRows.indexOf(cell.parentElement);
  if (rowIdx < 0) return null;
  const grid = buildGrid(allRows);
  const row = grid[rowIdx] || [];
  let colIndex = -1;
  for (let c = 0; c < row.length; c++) if (row[c] === cell) { colIndex = c; break; }
  if (colIndex < 0) return null;
  const headerRow = thead && thead.rows.length ? grid[thead.rows.length - 1] || [] : [];
  const hc = headerRow[colIndex];
  return { colIndex, label: hc ? norm(hc.textContent) : `Col ${colIndex + 1}` };
}

// Add/remove the cell under `el` — or, on a column header, the whole column.
// Returns true if something was actually toggled (so the caller knows to
// swallow the event instead of opening a menu).
export function togglePick(el) {
  if (!(el instanceof Element)) return false;
  const th = el.closest("th");
  if (th && th.closest("thead")) return togglePickColumn(th);
  const cell = el.closest("td");
  if (!cell || cell.closest("thead")) return false;
  const table = cell.closest(TABLE_SELECTOR);
  if (!table) return false;

  const at = picks.items.findIndex((p) => p.cell === cell);
  if (at >= 0) {
    cell.classList.remove(PICK_CLASS);
    picks.items.splice(at, 1);
  } else {
    if (picks.items.length && picks.items[0].table !== table) clearPicks(true);
    const col = cellColumn(table, cell);
    if (!col) return false;
    const text = cellText(cell);
    cell.classList.add(PICK_CLASS);
    picks.items.push({ cell, table, colIndex: col.colIndex, label: col.label, text, number: parseNumber(text) });
  }
  emit();
  return true;
}

// Alt + right-click a column HEADER picks every visible cell of that column,
// or drops them all when the whole column is already picked. It is the same
// selection as picking the cells one by one, so the NowBar, Math and Create
// Doc all see it.
function togglePickColumn(th) {
  const table = th.closest(TABLE_SELECTOR);
  if (!table || !table.tBodies[0]) return false;
  const colIndex = headerColIndex(table, th);
  if (colIndex < 0) return false;
  const label = norm(th.textContent) || `Col ${colIndex + 1}`;
  if (/^actions?$/i.test(label)) return false;

  const rows = Array.from(table.tBodies[0].rows);
  const grid = buildGrid(rows);
  const cells = [];
  const seen = new Set(); // a rowspan cell covers several rows — pick it once
  rows.forEach((row, r) => {
    if (!isDataRow(row)) return;
    const cell = grid[r] ? grid[r][colIndex] : null;
    // colSpan > 1 = a full-width note / subtotal cell, not this column's data.
    if (!cell || seen.has(cell) || (cell.colSpan || 1) > 1) return;
    seen.add(cell);
    cells.push(cell);
  });
  if (!cells.length) return true;

  if (picks.items.length && picks.items[0].table !== table) clearPicks(true);
  const picked = new Set(picks.items.map((p) => p.cell));
  if (cells.every((c) => picked.has(c))) {
    const drop = new Set(cells);
    for (const c of cells) c.classList.remove(PICK_CLASS);
    picks.items = picks.items.filter((p) => !drop.has(p.cell));
  } else {
    for (const cell of cells) {
      if (picked.has(cell)) continue;
      const text = cellText(cell);
      cell.classList.add(PICK_CLASS);
      picks.items.push({ cell, table, colIndex, label, text, number: parseNumber(text) });
    }
  }
  emit();
  return true;
}

// Drop picks whose cell React has since replaced — a stale node can never be
// un-highlighted or re-read, so it must not linger in the selection.
function prunePicks() {
  const live = picks.items.filter((p) => p.cell.isConnected);
  if (live.length !== picks.items.length) picks.items = live;
  return picks.items;
}

export function getPicks() {
  const items = prunePicks();
  const numbers = items.map((p) => p.number).filter((n) => n !== null);
  const cols = new Set(items.map((p) => p.colIndex));
  return {
    table: items[0]?.table || null,
    items: items.map(({ colIndex, label, text, number }) => ({ colIndex, label, text, number })),
    numbers,
    // Same-column selections can drive column-scoped filters directly; a
    // mixed-column selection can still be used for arithmetic.
    sameColumn: cols.size === 1 ? items[0].colIndex : null,
  };
}

export function clearPicks(silent) {
  for (const p of picks.items) p.cell.classList.remove(PICK_CLASS);
  picks.items = [];
  if (!silent) emit();
}

// ── Export to Docs (right-click → Create Doc) ──────────────────────────────
// Turns on-screen cells into { columns, rows } for POST /docs/push-table.
// Values are what the filter reads (cellText), so a grouped cell exports its
// full `data-qf-text` label, and a row-spanning cell repeats on every row it
// covers — a sheet wants one self-contained value per cell, not merges.

// Numbers that are really identifiers. Sent as numbers they'd pick up the
// sheet's #,##0 format ("PIN 416,001") and be summable nonsense. "No. of
// pieces" is a count, though, so "no/number of" doesn't count as an id.
const ID_LABEL_RE = /\b(?:no|number|num|code|id|phone|mobile|contact|pin|pincode|zip|year|fy|gstin?|pan|hsn|ifsc|account)\b(?!\.?\s*of\b)/i;
const LEADING_ZERO_RE = /^\D*0\d/; // "0042" would lose its zeros as a number
const HAS_CURRENCY_RE = /[₹$€£¥]/; // not CURRENCY_RE: that one is /g (stateful .test)

// A column goes out as numbers only when EVERY filled cell is a plain number,
// so one stray text value keeps the column text and nothing gets dropped.
function exportColumnType(label, texts) {
  const filled = texts.filter((t) => !isBlankText(t));
  if (!filled.length || ID_LABEL_RE.test(label)) return "text";
  const numeric = filled.every((t) =>
    parseNumber(t) !== null && !LEADING_ZERO_RE.test(t) && t.replace(/\D/g, "").length <= 15);
  if (!numeric) return "text";
  return filled.some((t) => HAS_CURRENCY_RE.test(t)) ? "money" : "number";
}

function buildExport(table, rowIdxs, colIdxs) {
  const bodyRows = Array.from(table.tBodies[0].rows);
  const grid = buildGrid(bodyRows);
  const headerRow = table.tHead && table.tHead.rows.length
    ? buildGrid(Array.from(table.rows))[table.tHead.rows.length - 1] || []
    : [];
  const cols = colIdxs
    .map((c) => ({ colIndex: c, label: (headerRow[c] ? norm(headerRow[c].textContent) : "") || `Col ${c + 1}` }))
    .filter((c) => !/^actions?$/i.test(c.label));
  if (!cols.length || !rowIdxs.length) return null;

  const texts = rowIdxs.map((r) => cols.map((c) => {
    const cell = grid[r] ? grid[r][c.colIndex] : null;
    return cell ? cellText(cell) : "";
  }));
  const columns = cols.map((c, i) => {
    const colTexts = texts.map((row) => row[i]);
    const longest = colTexts.slice(0, 300).reduce((m, t) => Math.max(m, t.length), c.label.length);
    return {
      label: c.label.slice(0, 60),
      type: exportColumnType(c.label, colTexts),
      width: Math.min(360, Math.max(70, Math.round(longest * 7.5 + 20))),
    };
  });
  const rows = texts.map((row) => row.map((t, i) => {
    if (isBlankText(t)) return "";
    return columns[i].type === "text" ? t : parseNumber(t);
  }));
  return { columns, rows };
}

// The current Alt-pick selection as a table: every row that holds a picked
// cell × every column that holds one. Picking one cell per row selects those
// rows; picking a header selects that column.
export function selectionExport() {
  const items = prunePicks();
  if (!items.length) return null;
  const table = items[0].table;
  if (!table.isConnected || !table.tBodies[0]) return null;
  const bodyRows = Array.from(table.tBodies[0].rows);
  const rowSet = new Set();
  const colSet = new Set();
  for (const p of items) {
    const r = bodyRows.indexOf(p.cell.parentElement);
    if (r < 0) continue;
    const span = p.cell.rowSpan || 1;
    for (let k = r; k < r + span && k < bodyRows.length; k++) rowSet.add(k);
    colSet.add(p.colIndex);
  }
  const rowIdxs = [...rowSet].filter((r) => isDataRow(bodyRows[r])).sort((a, b) => a - b);
  const colIdxs = [...colSet].sort((a, b) => a - b);
  return buildExport(table, rowIdxs, colIdxs);
}

// Data rows currently on screen (i.e. after any Quick Filter).
function exportRowIdxs(table) {
  if (!table || !table.tBodies[0]) return [];
  return Array.from(table.tBodies[0].rows)
    .map((row, r) => (isDataRow(row) ? r : -1))
    .filter((r) => r >= 0);
}

export function exportRowCount(table) {
  return exportRowIdxs(table).length;
}

// The whole table as on screen: visible rows × every named column.
export function tableExport(table) {
  if (!table || !table.isConnected) return null;
  return buildExport(table, exportRowIdxs(table), describeColumns(table).map((c) => c.colIndex));
}

// ── Predicates ─────────────────────────────────────────────────────────────
// A filter targets one column (`colIndex`) or several (`colIndexes`). With
// several, `any: true` means a row passes if ANY of those columns matches —
// which is what "search these 3 columns for PARIKH" needs.
export function filterColumns(f) {
  return f.colIndexes && f.colIndexes.length ? f.colIndexes : [f.colIndex];
}

function rowMatches(rowCells, f) {
  const idxs = filterColumns(f);
  const test = (i) => cellMatches(rowCells ? rowCells[i] : null, f);
  return f.any ? idxs.some(test) : idxs.every(test);
}

function cellMatches(cell, f) {
  const raw = cellText(cell);
  switch (f.type) {
    case "eq":
      return raw === f.value;
    case "in":
      return f.values.includes(displayValue(raw, f.kind));
    case "contains":
      return raw.toLowerCase().includes(f.text.toLowerCase());
    case "notcontains":
      return !raw.toLowerCase().includes(f.text.toLowerCase());
    case "blank":
      return isBlankText(raw);
    case "notblank":
      return !isBlankText(raw);
    case "num": {
      const n = parseNumber(raw);
      if (n === null) return false;
      const { op, a, b } = f;
      if (op === "eq") return n === a;
      if (op === "ne") return n !== a;
      if (op === "gt") return n > a;
      if (op === "gte") return n >= a;
      if (op === "lt") return n < a;
      if (op === "lte") return n <= a;
      if (op === "between") return n >= Math.min(a, b) && n <= Math.max(a, b);
      return true;
    }
    case "date": {
      const d = parseDate(raw);
      if (d === null) return false;
      if (f.from != null && d < f.from) return false;
      if (f.to != null && d > f.to) return false;
      return true;
    }
    default:
      return true;
  }
}

// Human-readable chip text for an active filter.
const NUM_OP_LABEL = { eq: "=", ne: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤" };
const fmtNum = (n) => (n === null || n === undefined ? "" : n.toLocaleString("en-IN"));

export function filterSummary(f) {
  switch (f.type) {
    case "eq": return f.value;
    case "in": return f.values.length === 1 ? f.values[0] : `${f.values.length} values`;
    case "contains": return `contains "${f.text}"`;
    case "notcontains": return `excludes "${f.text}"`;
    case "blank": return "is blank";
    case "notblank": return "is not blank";
    case "num":
      return f.op === "between"
        ? `${fmtNum(Math.min(f.a, f.b))} – ${fmtNum(Math.max(f.a, f.b))}`
        : `${NUM_OP_LABEL[f.op] || ""} ${fmtNum(f.a)}`.trim();
    case "date":
      if (f.from != null && f.to != null) return `${fmtDate(f.from)} – ${fmtDate(f.to)}`;
      if (f.from != null) return `from ${fmtDate(f.from)}`;
      if (f.to != null) return `up to ${fmtDate(f.to)}`;
      return "any date";
    default: return "";
  }
}

// ── Active-filter store (single global instance) ───────────────────────────
const state = {
  table: null,        // the <table> currently under a filter
  filters: [],        // [{ colIndex, label, type, ... }] — AND-combined
  observer: null,     // re-applies after React re-renders the rows
  scheduled: false,
  listeners: new Set(),
};

function snapshot() {
  return {
    active: state.filters.length > 0,
    table: state.table,
    filters: state.filters.map((f) => ({ ...f, summary: filterSummary(f) })),
    pickCount: prunePicks().length,
  };
}

export function getActive() {
  return snapshot();
}

// The filter currently applied to `colIndex` of `table`, or null — lets the
// header panel open pre-filled with what's already active.
export function getColumnFilter(table, colIndex) {
  if (!table || state.table !== table) return null;
  return state.filters.find((f) => filterColumns(f).includes(colIndex)) || null;
}

export function subscribe(fn) {
  state.listeners.add(fn);
  return () => state.listeners.delete(fn);
}

function emit() {
  const snap = snapshot();
  state.listeners.forEach((fn) => {
    try { fn(snap); } catch { /* listener error must not break the store */ }
  });
}

function clearRows() {
  const t = state.table;
  if (!t || !t.isConnected) return;
  const tbody = t.tBodies[0];
  if (tbody) for (const row of Array.from(tbody.rows)) row.classList.remove(HIDDEN_CLASS);
  markHeaders(true);
}

// Highlight the header of every filtered column. Re-run from apply() because a
// React re-render replaces the <th> nodes and wipes the class.
function markHeaders(clearOnly) {
  const t = state.table;
  if (!t || !t.isConnected || !t.tHead) return;
  for (const el of Array.from(t.querySelectorAll("." + COL_ACTIVE_CLASS))) {
    el.classList.remove(COL_ACTIVE_CLASS);
  }
  if (clearOnly) return;
  const headerRow = buildGrid(Array.from(t.rows))[t.tHead.rows.length - 1] || [];
  for (const f of state.filters) {
    for (const idx of filterColumns(f)) {
      const hc = headerRow[idx];
      if (hc) hc.classList.add(COL_ACTIVE_CLASS);
    }
  }
}

function apply() {
  const table = state.table;
  if (!table || !table.isConnected) { clearAll(); return; }
  const tbody = table.tBodies[0];
  if (!tbody) return;
  const rows = Array.from(tbody.rows);
  const grid = buildGrid(rows);
  for (let r = 0; r < rows.length; r++) {
    let show = true;
    for (const f of state.filters) {
      if (!rowMatches(grid[r], f)) { show = false; break; }
    }
    rows[r].classList.toggle(HIDDEN_CLASS, !show);
  }
  // Summary rows follow the section ABOVE them (back to the previous header or
  // summary): visible while any row they total is still on screen.
  for (let r = 0; r < rows.length; r++) {
    if (!rows[r].hasAttribute("data-qf-summary")) continue;
    let sectionVisible = false;
    for (let k = r - 1; k >= 0; k--) {
      if (isGroupHeaderRow(rows[k])) break;
      if (!rows[k].classList.contains(HIDDEN_CLASS)) { sectionVisible = true; break; }
    }
    rows[r].classList.toggle(HIDDEN_CLASS, !sectionVisible);
  }
  // Group-header rows (e.g. the state banner on Customers) carry no column data
  // of their own, so a filter would always hide them. Instead, tie each one to
  // its section: visible while any row beneath it survives, hidden once the
  // whole section is filtered out. Consecutive headers (e.g. Extend Mode's
  // spacer + PO info rows) share the section that follows them.
  for (let r = rows.length - 1; r >= 0; r--) {
    if (!rows[r].hasAttribute("data-qf-group-header")) continue;
    let sectionVisible = false;
    let k = r + 1;
    while (k < rows.length && rows[k].hasAttribute("data-qf-group-header")) k++;
    for (; k < rows.length; k++) {
      if (rows[k].hasAttribute("data-qf-group-header")) break;
      if (!rows[k].classList.contains(HIDDEN_CLASS)) { sectionVisible = true; break; }
    }
    rows[r].classList.toggle(HIDDEN_CLASS, !sectionVisible);
  }
  markHeaders();
}

// Coalesce the bursts of mutations React emits while re-rendering a table.
function scheduleApply() {
  if (state.scheduled) return;
  state.scheduled = true;
  Promise.resolve().then(() => { state.scheduled = false; apply(); });
}

function setupObserver() {
  if (state.observer) state.observer.disconnect();
  if (!state.table) return;
  // childList only (not attributes) — our own class toggles are attribute
  // mutations, so this can't feed back into itself; row add/remove from a
  // React re-render does fire it, which is when we need to re-apply.
  state.observer = new MutationObserver(scheduleApply);
  state.observer.observe(state.table, { childList: true, subtree: true });
}

// Add or replace the filter on one column. `descriptor` is
// { table, colIndex, label, type, ...params } — see cellMatches() for the
// params each type takes.
export function setFilter(descriptor) {
  if (!descriptor || !descriptor.table || !descriptor.type) return;
  const { table } = descriptor;
  if (state.table !== table) {
    clearRows();
    state.table = table;
    state.filters = [];
    setupObserver();
  }
  const { table: _t, ...filter } = descriptor;
  // One filter per column set: re-filtering the same columns replaces, a
  // different set adds and AND-combines.
  filter.key = filterColumns(filter).slice().sort((a, b) => a - b).join(",");
  const at = state.filters.findIndex((f) => f.key === filter.key);
  if (at >= 0) state.filters[at] = filter;
  else state.filters.push(filter);
  apply();
  emit();
}

// Exact-match shortcut used by the right-click menu. `match` comes from the
// clicked cell's `data-qf-match` — a group header filters by "contains" so it
// selects its whole section, everything else stays an exact match.
export function applyFilter({ table, colIndex, label, value, match }) {
  if (match === "contains") {
    setFilter({ table, colIndex, label, type: "contains", text: value });
  } else {
    setFilter({ table, colIndex, label, type: "eq", value });
  }
}

// Drop one filter. Takes its key, or — for a single-column filter, whose key
// IS its column index — just the colIndex.
export function removeFilter(keyOrColIndex) {
  const key = String(keyOrColIndex);
  const next = state.filters.filter((f) => f.key !== key);
  if (next.length === 0) { clearAll(); return; }
  // Un-hide first, then re-apply the survivors, so rows only this filter was
  // hiding come back.
  clearRows();
  state.filters = next;
  apply();
  emit();
}

export function clearAll() {
  clearRows();
  if (state.observer) { state.observer.disconnect(); state.observer = null; }
  state.table = null;
  state.filters = [];
  emit();
}
